/**
 * use-accounts — reactive view over the web multi-account registry + the
 * switch/add/remove orchestration used by the account switcher UI.
 *
 * Switching an account is data-preserving: we set the active pointer, re-prime
 * the session actor for the new identity and let the PowerSync provider swap to
 * that user's DB instance (it reacts to the active user id). We NEVER call
 * `destroyAccountData` on a switch — that would destroy the data. Only an
 * explicit "Sign out" / "Remove account" wipes a database.
 */

import { createSignal, onCleanup, onMount } from "solid-js";
import { useNavigate } from "@tanstack/solid-router";
import * as routerPkg from "@tanstack/solid-router";
import { type AccountsState, getActiveAccount, type StoredAccount } from "@stockpilot/shared";
import { ACCOUNTS_CHANGED_EVENT, accountsStore, readAccountsStateSync } from "@/lib/accounts";
import { resolveTabAccount } from "@/lib/active-account";
import {
  activeTabAccountId,
  TAB_ACCOUNT_CHANGED_EVENT,
  writeTabAccountId,
} from "@/lib/tab-account";
import { pruneAliases } from "@/lib/account-alias";
import { primeSessionForAccount } from "@/lib/session-machine";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { queryClient } from "@/lib/query-client";

export function useAccounts() {
  const navigate = useNavigate();
  const routerPkgObj = routerPkg as {
    useRouter?: (opts?: { warn?: boolean }) => { state?: { matches?: { fullPath?: string }[] } };
  };
  const router =
    "useRouter" in routerPkgObj && typeof routerPkgObj.useRouter === "function"
      ? routerPkgObj.useRouter({ warn: false })
      : undefined;
  const { destroyAccountData } = usePowerSyncContext();
  const [state, setState] = createSignal<AccountsState>(readAccountsStateSync());

  // localStorage is the source of truth; reload on mount and on cross-tab
  // writes so a switch/add/remove in another tab is reflected here too.
  const reload = () => {
    void (async () => {
      setState(await accountsStore.load());
    })();
  };

  onMount(() => {
    reload();
    // Cross-tab: the `storage` event fires in OTHER tabs.
    const onStorage = (e: StorageEvent) => {
      if (e.key === "accounts" || e.key === null) {
        reload();
      }
    };
    // Same-tab: `storage` never fires for our own writes, so a sign-out/remove in
    // THIS tab must be picked up via the registry's custom change event.
    const onLocalChange = () => {
      reload();
    };
    globalThis.addEventListener("storage", onStorage);
    globalThis.addEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
    globalThis.addEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    onCleanup(() => {
      globalThis.removeEventListener("storage", onStorage);
      globalThis.removeEventListener(TAB_ACCOUNT_CHANGED_EVENT, onLocalChange);
      globalThis.removeEventListener(ACCOUNTS_CHANGED_EVENT, onLocalChange);
    });
  });

  const accounts = () => state().accounts;

  // Per-tab active account resolved from THIS tab's pin in sessionStorage
  // (`activeTabAccountId()`), NOT from the global registry default.
  const active = (): StoredAccount | null => {
    const tabPin = activeTabAccountId();
    return resolveTabAccount(state(), tabPin);
  };
  const activeUserId = () => active()?.userId ?? null;

  /**
   * Switches THIS TAB to a known account WITHOUT clearing any data, pinning
   * `userId` into `sessionStorage` and navigating to the clean root `/`.
   *
   * SECTION 7.0b ORDERING:
   * 1. `queryClient.clear()` drops cached query data.
   * 2. `primeSessionForAccount()` sets synchronous machine context.
   * 3. `writeTabAccountId()` flips the pin AND emits notification.
   * 4. `navigate({ to: "/" })` commits the navigation.
   */
  const switchTo = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);

    // D1/D2: Preserve current route shape on account switch.
    // If the matched route has entity params ($), fall back to section ancestor.
    const currentMatches = router?.state?.matches;
    const currentMatch =
      currentMatches?.findLast?.((m) => Boolean(m.fullPath)) ??
      (currentMatches && currentMatches.length > 0 ? currentMatches.at(-1) : undefined);
    const fullPath = currentMatch?.fullPath;
    if (fullPath && fullPath.includes("$")) {
      const ancestor = fullPath.split("$")[0]?.replace(/\/+$/, "");
      await navigate({ to: ancestor || "/" });
    }
  };

  /**
   * Switches THIS TAB to another account IN PLACE (staying on the current URL).
   * Used by the "Try another account" 404 picker when accessing shared links.
   */
  const switchHere = async (userId: string) => {
    const next = await accountsStore.setActive(userId);
    setState(next);
    queryClient.clear();
    primeSessionForAccount(getActiveAccount(next));
    writeTabAccountId(userId);
  };

  /**
   * Opens the login screen in "add" mode to add another account WITHOUT
   * signing the current one out.
   */
  const addAccount = async () => {
    await navigate({ to: "/login", search: { redirect: "/", mode: "add" } });
  };

  /**
   * Activates the explicit guest account in THIS tab.
   */
  const continueAsGuest = async () => {
    const next = await accountsStore.ensureGuestActive();
    setState(next);
    const guest = getActiveAccount(next);
    queryClient.clear();
    primeSessionForAccount(guest);
    if (guest) {
      writeTabAccountId(guest.userId);
    }
    // D5: Guest transitions cross a privilege boundary, safe landing on root is deliberate.
    await navigate({ to: "/" });
  };

  /**
   * Removes an account from the registry and destroys its local database. If it
   * was THIS TAB's active account, falls back to a remaining account.
   */
  const removeAccount = async (account: StoredAccount) => {
    const wasActive = activeUserId() === account.userId;
    const next = await accountsStore.remove(account.userId);
    setState(next);
    pruneAliases(next.accounts.map((a) => a.userId));
    await destroyAccountData(account.userId);
    if (wasActive) {
      const fallback = getActiveAccount(next);
      queryClient.clear();
      primeSessionForAccount(fallback);
      if (fallback) {
        writeTabAccountId(fallback.userId);
        await navigate({ to: "/" });
      } else {
        await navigate({ to: "/login" });
      }
    }
  };

  return {
    accounts,
    activeUserId,
    active,
    activeAccount: active,
    switchTo,
    switchHere,
    addAccount,
    continueAsGuest,
    removeAccount,
    reload,
  };
}
